import type { DefaultLabelFormatterCallbackParams, EChartsOption } from 'echarts';
import ObjCheck from '../../utils/objCheck';
import ReportUtils from '../../utils/reportUtils';
import DescriptorType from '../../utils/descriptor/descriptorType';
import { NAMES_TO_SCALES_MAP } from '../../utils/scaleUtil';
import {
    AnnotationType,
    AnnotationTypeValue,
    AxisConfig,
    ChartConfig,
    ChartDescriptor,
    ChartReport,
    ChartSign,
    DataPoint,
    DataSourceState,
    FieldConfig,
    LegendItem,
    SeriesType,
} from './types';
import { FONT_FAMILY_CHART, RIGHT_AXIS_STEP, ROTATE_LABEL_THRESHOLD } from './constants';
import Assert from '../../utils/asserts';

// ─── Field type helpers ───────────────────────────────────────────────────────

export function isFieldPercent(fieldCfg: FieldConfig): boolean {
    return fieldCfg.descriptorType === DescriptorType.PERCENT;
}

/** Used during fieldsCfg initialisation — takes the raw descriptor, not FieldConfig */
export function isDescriptorRatio(descriptor: ChartDescriptor): boolean {
    return descriptor.viewScale === NAMES_TO_SCALES_MAP.Actual && !DescriptorType.isPercent(descriptor);
}

/** Returns true for descriptors that can be plotted (number or percent types) */
export function chartableDescriptorsMatcher(descriptor: ChartDescriptor): boolean {
    return DescriptorType.isNumber(descriptor) || DescriptorType.isPercent(descriptor);
}

// ─── Report / period helpers ──────────────────────────────────────────────────

export function getChartReports(reports: ChartReport[], selectedPeriods: string[]): ChartReport[] {
    const periodSet = new Set(selectedPeriods);
    return reports.filter((r) => periodSet.has(r.period));
}

export function getFieldRawValue(
    report: ChartReport,
    descriptorId: string,
    showOriginalDataIfLatestIsMissing: boolean,
): unknown {
    let rawValue = ReportUtils.getFieldValueOrP2PCalc(report, descriptorId);
    if (
        ObjCheck.isNullUndefinedEmptyDashOrSpace(rawValue) &&
        ReportUtils.hasDataDiffExpectation(report, descriptorId) &&
        showOriginalDataIfLatestIsMissing
    ) {
        rawValue = ReportUtils.getDataDiffExpectation(report, descriptorId);
    }
    return rawValue;
}

// ─── Key helpers ─────────────────────────────────────────────────────────────

/** Composite key that uniquely identifies a field within a specific data source. */
export function getSeriesKey(sourceId: string | number, fieldId: string): string {
    return `${sourceId}_${fieldId}`;
}

// ─── Heading helpers ─────────────────────────────────────────────────────────

/** Auto-generate a heading from selected fields: join with ' vs. ' if ≤ 2, else empty */
export function autogenerateHeading(fieldsCfg: Record<string, FieldConfig>): string {
    const selectedFields = Object.values(fieldsCfg).filter((f) => f.chartSign !== ChartSign.OFF);
    if (selectedFields.length <= 2) {
        return selectedFields.map((f) => f.label).join(' vs. ');
    }
    return '';
}

// ─── Label helpers ────────────────────────────────────────────────────────────

export function getFieldLabel(label: string, totalChange: number | null): string {
    if (totalChange === null) {
        return label;
    }
    const sign = totalChange >= 0 ? '+' : '';
    return `${label} (Total Change: ${sign}${totalChange.toFixed(1)}%)`;
}

// ─── Data transformation ──────────────────────────────────────────────────────

/**
 * Converts a raw field value to a number.
 * String values: commas are treated as decimal separators (European format)
 * and percent signs are stripped before parsing.
 */
export function dataToNumber(value: unknown): number | null {
    let result: number;
    if (typeof value === 'number') {
        result = value;
    } else if (typeof value === 'string') {
        result = Number(value.replace(',', '.').replace('%', ''));
    } else {
        Assert.trueVal(
            false,
            `Unexpected type "${typeof value}" for value "${value}" in dataToNumber — expected number or string`,
        );
        result = Number(value);
    }
    Assert.trueVal(!Number.isNaN(result), `Invalid number: ${value} in dataToNumber in charts`);
    return Number.isNaN(result) ? null : result;
}

const compactFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2,
});
const longFormatter = new Intl.NumberFormat('en-US', {
    notation: 'standard',
    maximumFractionDigits: 2,
});

/**
 * Computes the annotation label for a single data point.
 *
 * Edge cases:
 * - First data point (no previous value): returns `'0.00%'` for growth % mode
 *   to match original behaviour (no prior reference point).
 * - Division by zero (previous value is 0): returns `'-%'` since percentage
 *   change is undefined.
 */
function computeAnnotation(
    annotationType: AnnotationTypeValue,
    fieldId: string,
    currentValue: number,
    fieldCfg: FieldConfig,
    prevValues: Record<string, number | undefined>,
): string {
    if (annotationType === AnnotationType.GrowthPercent) {
        const prev = prevValues[fieldId];
        if (prev === undefined) {
            return '0.00%';
        }
        if (prev === 0) {
            return '-%';
        }
        const change = ((currentValue - prev) / Math.abs(prev)) * 100;
        return change.toFixed(2) + '%';
    }

    // Value annotation
    if (isFieldPercent(fieldCfg)) {
        return `${currentValue}%`;
    }
    return compactFormatter.format(currentValue);
}

// ─── Axis configuration ───────────────────────────────────────────────────────

export function computeAxisConfig(fieldIdsToChart: string[], fieldsCfg: Record<string, FieldConfig>): AxisConfig {
    // Only fields NOT on a separate axis contribute to the shared base axes.
    // Fields with separateAxis: true bypass the shared axes entirely.
    // Exclude them so they don't create phantom shared axes.
    const sharedIds = fieldIdsToChart.filter((id) => !fieldsCfg[id].separateAxis);

    const hasNumber = sharedIds.some((id) => !isFieldPercent(fieldsCfg[id]) && !fieldsCfg[id].ratio);
    const hasRatio = sharedIds.some((id) => fieldsCfg[id].ratio);
    const hasPercent = sharedIds.some((id) => isFieldPercent(fieldsCfg[id]));

    let nextIdx = 0;
    const numberAxisIndex = hasNumber ? nextIdx++ : -1;
    const ratioAxisIndex = hasRatio ? nextIdx++ : -1;
    const percentAxisIndex = hasPercent ? nextIdx++ : -1;

    const separateAxisIndices: Record<string, number> = {};
    for (const id of fieldIdsToChart) {
        if (fieldsCfg[id].separateAxis) {
            separateAxisIndices[id] = nextIdx++;
        }
    }

    return { hasNumber, hasRatio, hasPercent, numberAxisIndex, ratioAxisIndex, percentAxisIndex, separateAxisIndices };
}

// ─── ECharts option builder ───────────────────────────────────────────────────

const formatShort = (value: number) => compactFormatter.format(value);
const formatPercent = (value: number) => `${value}%`;

const axisLabelStyle = { fontSize: 14, fontWeight: 500 } as const;

const SERIES_LINE_STYLE = { width: 4 } as const;
const SERIES_EMPHASIS_LINE_STYLE = { width: 5 } as const;

const BLUR_STYLE = {
    lineStyle: { opacity: 0.15 },
    itemStyle: { opacity: 0.15 },
    label: { opacity: 0.3 },
} as const;

const CHART_GRID = { left: 60, right: 0, top: 45, bottom: 45 } as const;

export function buildYAxes(
    axisConfig: AxisConfig,
    startFromZero: boolean,
    showGridLines: boolean,
    separateFields: Array<{ compositeKey: string; fieldCfg: FieldConfig }>,
): EChartsOption['yAxis'] {
    const { hasNumber, hasRatio, hasPercent } = axisConfig;
    const axes: EChartsOption['yAxis'] = [];

    const baseAxisStyle = {
        type: 'value' as const,
        axisLine: { show: false },
        axisTick: { show: false },
    };

    const scaleOption = startFromZero ? {} : { scale: true };

    if (hasNumber) {
        axes.push({
            ...baseAxisStyle,
            position: 'left',
            ...scaleOption,
            splitLine: { show: showGridLines },
            axisLabel: { ...axisLabelStyle, formatter: formatShort },
        });
    }

    if (hasRatio) {
        const isPrimary = !hasNumber;
        const position = isPrimary ? ('left' as const) : ('right' as const);

        axes.push({
            ...baseAxisStyle,
            position,
            ...(isPrimary ? {} : { alignTicks: true }),
            ...scaleOption,
            splitLine: { show: showGridLines },
            axisLabel: { ...axisLabelStyle, formatter: formatShort },
        });
    }

    if (hasPercent) {
        const isPrimary = !hasNumber && !hasRatio;
        const position = isPrimary ? ('left' as const) : ('right' as const);
        const offset = hasNumber && hasRatio ? RIGHT_AXIS_STEP : 0;

        axes.push({
            ...baseAxisStyle,
            position,
            offset,
            ...(isPrimary ? {} : { alignTicks: true }),
            ...scaleOption,
            splitLine: { show: showGridLines },
            axisLabel: { ...axisLabelStyle, formatter: formatPercent },
        });
    }

    // Separate axes stack after the shared right axes, so their offsets must
    // start beyond whatever shared right axes already exist.
    const baseRightCount =
        (hasRatio && hasNumber ? 1 : 0) +
        (hasPercent && (hasNumber || hasRatio) ? 1 : 0);

    const hasSharedAxes = hasNumber || hasRatio || hasPercent;

    separateFields.forEach(({ fieldCfg }, i) => {
        // When no shared axes exist, the first separate axis is responsible for
        // gridlines. Otherwise suppress them to avoid duplicate grids.
        const showSplitLine = showGridLines && !hasSharedAxes && i === 0;
        axes.push({
            ...baseAxisStyle,
            position: 'right',
            offset: (baseRightCount + i) * RIGHT_AXIS_STEP,
            alignTicks: true,
            ...scaleOption,
            splitLine: { show: showSplitLine },
            axisLabel: {
                ...axisLabelStyle,
                formatter: isFieldPercent(fieldCfg) ? formatPercent : formatShort,
            },
        });
    });

    return axes;
}

type BuildEChartsOptionParams = {
    periodLabels: string[];
    fieldIdsToChart: string[];
    fieldsCfg: Record<string, FieldConfig>;
    seriesData: Record<string, DataPoint[]>;
    chartCfg: ChartConfig;
    axisConfig: AxisConfig;
    colors: readonly string[];
    hiddenSeries: Set<string>;
    seriesLabelMap?: Map<string, string>;
};

export function buildEChartsOption({
    periodLabels,
    fieldIdsToChart,
    fieldsCfg,
    seriesData,
    chartCfg,
    axisConfig,
    colors,
    hiddenSeries,
    seriesLabelMap,
}: BuildEChartsOptionParams): EChartsOption {
    const { hasNumber, hasRatio, hasPercent, numberAxisIndex, ratioAxisIndex, percentAxisIndex, separateAxisIndices } = axisConfig;

    const rightAxisCount = [hasRatio && hasNumber, hasPercent && (hasNumber || hasRatio)].filter(Boolean).length;
    const separateAxisCount = Object.keys(separateAxisIndices).length;
    const totalRightAxes = rightAxisCount + separateAxisCount;
    const gridRight = Math.max(RIGHT_AXIS_STEP, totalRightAxes * RIGHT_AXIS_STEP);

    const separateFields = fieldIdsToChart
        .filter((id) => separateAxisIndices[id] !== undefined)
        .map((id) => ({ compositeKey: id, fieldCfg: fieldsCfg[id] }));

    // We omit hidden series from the `series` array entirely instead of relying on
    // ECharts' `legend.selected` to hide them. ECharts' `labelLayout.hideOverlap`
    // evaluates label collisions across every series present in the option, regardless
    // of legend selection — so if a hidden series stayed in the array, its ghost labels
    // would keep suppressing annotations on the still-visible series. Filtering here is
    // what makes annotation overlap react correctly to legend toggles.
    const series: EChartsOption['series'] = fieldIdsToChart
        .map((id, index) => ({ id, index }))
        .filter(({ id }) => !hiddenSeries.has(id))
        .map(({ id, index }) => {
        const fieldCfg = fieldsCfg[id];
        // `index` is the position in the *unfiltered* fieldIdsToChart so each series
        // keeps the same color when other series are toggled on/off via the legend.
        // Re-indexing after the filter would cause colors to shift on every toggle.
        const color = colors[index % colors.length];

        let yAxisIndex: number;
        // Separate axis takes precedence — the field has opted out of the shared axes.
        if (separateAxisIndices[id] !== undefined) {
            yAxisIndex = separateAxisIndices[id];
        } else if (isFieldPercent(fieldCfg)) {
            yAxisIndex = percentAxisIndex;
        } else if (fieldCfg.ratio) {
            yAxisIndex = ratioAxisIndex;
        } else {
            yAxisIndex = numberAxisIndex;
        }

        const isBar = fieldCfg.seriesType === SeriesType.BARS;

        return {
            name: id,
            type: isBar ? 'bar' : 'line',
            yAxisIndex,
            connectNulls: true,
            itemStyle: { color },
            lineStyle: { ...SERIES_LINE_STYLE, color },
            symbolSize: 6,
            data: seriesData[id].map((point) => ({
                value: point.value,
                annotation: point.annotation,
            })),
            label: {
                show: chartCfg.showAnnotations && fieldCfg.annotationsEnabled,
                fontSize: chartCfg.annotationSize,
                color: chartCfg.matchSeriesColor ? color : '#000000',
                textBorderColor: "#FFFFFF",
                textBorderType: "solid",
                textBorderWidth: 1,
                formatter: (params: DefaultLabelFormatterCallbackParams) =>
                    (params.data as { annotation?: string })?.annotation ?? '',
                position: 'top',
            },
            labelLayout: {
                hideOverlap: true,
            },
            emphasis: {
                focus: 'series',
                lineStyle: SERIES_EMPHASIS_LINE_STYLE,
                itemStyle: { opacity: 1 },
            },
            blur: BLUR_STYLE,
        };
    });

    const shouldRotateLabels = periodLabels.length > ROTATE_LABEL_THRESHOLD;

    return {
        animation: true,
        grid: {
            ...CHART_GRID,
            right: gridRight,
            containLabel: false,
        },
        xAxis: {
            type: 'category',
            data: periodLabels,
            axisLine: { onZero: false },
            axisLabel: {
                fontFamily: FONT_FAMILY_CHART,
                fontSize: 13,
                interval: 0,
                rotate: shouldRotateLabels ? 45 : 0,
            },
        },
        yAxis: buildYAxes(axisConfig, chartCfg.startFromZero, chartCfg.showGridLines, separateFields),
        series,
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            textStyle: { fontFamily: 'verdana', fontSize: 12 },
            formatter: (params: unknown) => {
                if (!Array.isArray(params) || params.length === 0) {
                    return '';
                }
                const first = params[0] as { axisValueLabel?: string };
                const title = first.axisValueLabel ?? '';
                const lines = (params as { seriesName?: string; marker?: string; value?: unknown }[]).map((p) => {
                    const label =
                        seriesLabelMap?.get(p.seriesName as string) ??
                        fieldsCfg[p.seriesName as string]?.label ??
                        p.seriesName;
                    return `${p.marker} ${label}: ${p.value != null ? longFormatter.format(p.value as number) : '-'}`;
                });
                return `<strong>${title}</strong><br/>${lines.join('<br/>')}`;
            },
        },
        legend: {
            show: false,
            data: fieldIdsToChart,
            // No `selected` map here: hidden series are excluded from `series` above,
            // which is what allows `hideOverlap` to recompute against only visible
            // series. The visible legend UI is owned by the custom `chartLegend`
            // component, not ECharts.
        },
    };
}

// ─── Legend helpers ───────────────────────────────────────────────────────────

export function buildLegendItems(
    fieldIdsToChart: string[],
    fieldsCfg: Record<string, FieldConfig>,
    fieldsTotalChanges: Record<string, number | null>,
    colors: readonly string[],
    seriesLabelMap: Map<string, string>,
): LegendItem[] {
    return fieldIdsToChart.map((id, index) => ({
        id,
        label: seriesLabelMap.get(id) ?? fieldsCfg[id].label,
        color: colors[index % colors.length],
        totalChange: fieldsTotalChanges[id] ?? null,
    }));
}

// ─── buildChartData helpers ──────────────────────────────────────────────────

const PERIOD_ORDER: Record<string, number> = {
    Q1: 1,
    Q2: 2,
    H1: 3,
    Q3: 4,
    '9M': 5,
    Q4: 6,
    H2: 7,
    FY: 8,
};

type PeriodSlot = { year: number; period: string; slotIndex: number };

type UnifiedPeriodResult = { labels: string[]; slots: PeriodSlot[] };

export function buildUnifiedPeriodLabels(
    dataSources: DataSourceState[],
    selectedPeriods: string[],
    descending: boolean,
): UnifiedPeriodResult {
    // For each period label, find the max number of reports any single source has
    const maxCountPerLabel = new Map<string, { count: number; sortKey: number; year: number; period: string }>();

    for (const source of dataSources) {
        const chartReports = getChartReports(source.reports, selectedPeriods);
        const sourceCountPerLabel = new Map<string, number>();

        for (const report of chartReports) {
            const label = `${String(report.year).slice(2)} ${report.period}`;
            sourceCountPerLabel.set(label, (sourceCountPerLabel.get(label) ?? 0) + 1);

            if (!maxCountPerLabel.has(label)) {
                const sortKey = report.year * 100 + (PERIOD_ORDER[report.period] ?? 99);
                maxCountPerLabel.set(label, { count: 0, sortKey, year: report.year, period: report.period });
            }
        }

        for (const [label, count] of sourceCountPerLabel) {
            const entry = maxCountPerLabel.get(label)!;
            entry.count = Math.max(entry.count, count);
        }
    }

    const sorted = [...maxCountPerLabel.entries()].sort((a, b) =>
        descending ? b[1].sortKey - a[1].sortKey : a[1].sortKey - b[1].sortKey,
    );

    const labels: string[] = [];
    const slots: PeriodSlot[] = [];
    for (const [label, { count, year, period }] of sorted) {
        for (let i = 0; i < count; i++) {
            labels.push(label);
            slots.push({ year, period, slotIndex: i });
        }
    }

    return { labels, slots };
}

function buildReportLookup(chartReports: ChartReport[]): Map<string, ChartReport[]> {
    const map = new Map<string, ChartReport[]>();
    for (const report of chartReports) {
        const key = `${report.year}_${report.period}`;
        if (!map.has(key)) {
            map.set(key, []);
        }
        map.get(key)!.push(report);
    }
    return map;
}

/** Builds data points for one field by iterating over unified period slots. */
export function buildSeriesData(
    reportLookup: Map<string, ChartReport[]>,
    periodSlots: PeriodSlot[],
    fieldId: string,
    fieldCfg: FieldConfig,
    chartCfg: ChartConfig,
    showOriginalDataIfLatestIsMissing: boolean,
    compositeKey: string,
): DataPoint[] {
    const dataPoints: DataPoint[] = [];
    const prevValues: Record<string, number | undefined> = {};

    for (const slot of periodSlots) {
        const key = `${slot.year}_${slot.period}`;
        const report = reportLookup.get(key)?.[slot.slotIndex];

        if (!report) {
            dataPoints.push({ value: null, annotation: null });
            continue;
        }

        const rawValue = getFieldRawValue(report, fieldId, showOriginalDataIfLatestIsMissing);

        if (ObjCheck.isNullUndefinedEmptyDashOrSpace(rawValue)) {
            dataPoints.push({
                value: null,
                annotation: chartCfg.showAnnotations && fieldCfg.annotationsEnabled ? '-%' : null,
            });
            continue;
        }

        const parsed = dataToNumber(rawValue);
        if (parsed === null) {
            dataPoints.push({
                value: null,
                annotation: chartCfg.showAnnotations && fieldCfg.annotationsEnabled ? '-%' : null,
            });
            continue;
        }

        Assert.trueVal(
            fieldCfg.chartSign !== ChartSign.OFF,
            `chartSign is OFF for field "${fieldId}" in buildSeriesData — should have been filtered upstream`,
        );
        const numericValue = parsed * fieldCfg.chartSign;

        let annotation: string | null = null;
        if (chartCfg.showAnnotations && fieldCfg.annotationsEnabled) {
            annotation = computeAnnotation(chartCfg.annotationType, compositeKey, numericValue, fieldCfg, prevValues);
        }

        dataPoints.push({ value: numericValue, annotation });
        prevValues[compositeKey] = numericValue;
    }

    return dataPoints;
}

/** Computes total percentage change from first to last non-null value in a DataPoint array. */
export function computeTotalChange(dataPoints: DataPoint[]): number | null {
    const values = dataPoints.map((p) => p.value).filter((v): v is number => v !== null);
    if (values.length < 2) {
        return null;
    }
    const first = values[0];
    const last = values[values.length - 1];
    return first === 0 ? null : ((last - first) / Math.abs(first)) * 100;
}

/**
 * Builds a display label for a series, using structured companyName/schemaName.
 *
 * - Single source: "Revenue"
 * - Multi company, single schema: "Apple - Revenue"
 * - Multi company + multi schema: "Apple — Balance Sheet - Revenue"
 * - Single company + multi schema: "Balance Sheet - Revenue"
 */
export function buildSeriesLabel(
    companyName: string,
    schemaName: string,
    fieldLabel: string,
    multiCompany: boolean,
    multiSchema: boolean,
): string {
    if (!multiCompany && !multiSchema) {
        return fieldLabel;
    }
    if (multiCompany && multiSchema) {
        return `${companyName} — ${schemaName} - ${fieldLabel}`;
    }
    if (multiCompany) {
        return `${companyName} - ${fieldLabel}`;
    }
    return `${schemaName} - ${fieldLabel}`;
}

// ─── buildChartData ──────────────────────────────────────────────────────────

export type ChartData = {
    periodLabels: string[];
    fieldIdsToChart: string[];
    fieldsCfg: Record<string, FieldConfig>;
    seriesData: Record<string, DataPoint[]>;
    fieldsTotalChanges: Record<string, number | null>;
    seriesLabelMap: Map<string, string>;
    hasChartData: boolean;
};

/**
 * Converts the per-source store structure into the flat fieldsCfg + fieldIdsToChart
 * + merged reports that buildEChartsOption already accepts.
 *
 * Series keys are produced by `getSeriesKey(sourceId, fieldId)`.
 */
export function buildChartData(
    dataSources: DataSourceState[],
    selectedPeriods: string[],
    chartCfg: ChartConfig,
    showOriginalDataIfLatestIsMissing: boolean,
    descending: boolean = false,
): ChartData {
    const { labels: periodLabels, slots: periodSlots } = buildUnifiedPeriodLabels(
        dataSources,
        selectedPeriods,
        descending,
    );

    const uniqueCompanyIds = new Set(dataSources.map((s) => s.companyId));
    const uniqueSchemaIds = new Set(dataSources.map((s) => s.schemaId));
    const multiCompany = uniqueCompanyIds.size > 1;
    const multiSchema = uniqueSchemaIds.size > 1;

    const fieldIdsToChart: string[] = [];
    const flatFieldsCfg: Record<string, FieldConfig> = {};
    const flatSeriesData: Record<string, DataPoint[]> = {};
    const flatTotalChanges: Record<string, number | null> = {};
    const seriesLabelMap = new Map<string, string>();

    for (const source of dataSources) {
        const chartReports = getChartReports(source.reports, selectedPeriods);
        const reportLookup = buildReportLookup(chartReports);

        const sourceFieldIds = source.selectedFieldIds.filter(
            (id) => source.fieldsCfg[id]?.chartSign !== ChartSign.OFF,
        );

        for (const fieldId of sourceFieldIds) {
            const fieldCfg = source.fieldsCfg[fieldId];
            const compositeKey = getSeriesKey(source.id, fieldId);
            fieldIdsToChart.push(compositeKey);
            flatFieldsCfg[compositeKey] = fieldCfg;
            seriesLabelMap.set(
                compositeKey,
                buildSeriesLabel(
                    source.companyLegendLabel ?? source.companyName,
                    source.schemaLegendLabel ?? source.schemaName,
                    fieldCfg.label,
                    multiCompany,
                    multiSchema,
                ),
            );

            const dataPoints = buildSeriesData(
                reportLookup,
                periodSlots,
                fieldId,
                fieldCfg,
                chartCfg,
                showOriginalDataIfLatestIsMissing,
                compositeKey,
            );
            flatSeriesData[compositeKey] = dataPoints;
            flatTotalChanges[compositeKey] = computeTotalChange(dataPoints);
        }
    }

    return {
        periodLabels,
        fieldIdsToChart,
        fieldsCfg: flatFieldsCfg,
        seriesData: flatSeriesData,
        fieldsTotalChanges: flatTotalChanges,
        seriesLabelMap,
        hasChartData: periodLabels.length > 0 && fieldIdsToChart.length > 0,
    };
}
